import {
  UNIT_SUFFIXED_NUMBER_PATTERN,
  parseStrictDecimal,
  shiftPointLeft,
  stripInvisibleEdges,
  toCanonicalString,
} from "./decimal";
import { parseDateValue } from "./date";
import {
  formatDurationPartsForDisplay,
  parseEditedDurationValue,
} from "./duration";
import {
  ok,
  reject,
  viewValue,
  type IXDViewValue,
  type RawValue,
  type Result,
  type ValueContext,
} from "./types";

/**
 * 자유 입력 → canonical 중간값 (value-spec 05 엄격 파싱).
 *
 * user 소스의 toView 출력은 "표시 서식"이 아니라 toStored로 넘길 canonical
 * 중간값이다 — 숫자 kind는 콤마 없는 정규형("1,000"→"1000")이며, 콤마·괄호
 * 등 표시 서식은 storedToView 소관이다.
 *
 * - MONETARY/NUMERIC: strict 파싱(콤마 3자리 그룹, 괄호 음수, 하이픈 단독=0).
 *   단위 접미사("1,000원")는 unit-suffix, 그 외 실패는 not-numeric REJECT.
 * - PERCENT: "%" 접미면 ÷100("12%"→"0.12"), 없으면 그대로(OPEN #2 — 현행 계약).
 * - DATE: 4포맷 + 한국식(목표 동작) + 달력 검증 → dashed.
 * - DURATION: 한국어 파싱 → 한국어 정규 표기 (저장 포맷 변환은 toStored).
 * - BOOLEAN: passthrough — 검증은 toStored가 소유.
 * - TEXT: 원문 보존(strip 없음). 빈 입력은 모든 kind에서 검증 없이 통과.
 *
 * scale·negated·decimalDigits는 쓰지 않는다 — 저장 방향 적용(toStored)과
 * 표시 방향 적용(storedToView)으로 분리된 계약이다.
 */
export function toView(raw: RawValue, ctx: ValueContext): Result<IXDViewValue> {
  const result = toViewText(raw.text, ctx);
  return result.ok ? ok(viewValue(result.value)) : result;
}

// VO는 경계에서만 감싼다 — 내부 파싱은 문자열 기반으로 유지한다.
function toViewText(raw: string, ctx: ValueContext): Result<string> {
  if (ctx.kind === "TEXT") return ok(raw);

  const stripped = stripInvisibleEdges(raw);
  if (stripped === "") return ok("");

  switch (ctx.kind) {
    case "MONETARY":
    case "NUMERIC": {
      const parsed = parseStrictDecimal(stripped);
      if (parsed === null) {
        return UNIT_SUFFIXED_NUMBER_PATTERN.test(stripped)
          ? reject("unit-suffix", raw)
          : reject("not-numeric", raw);
      }
      return ok(toCanonicalString(parsed));
    }

    case "PERCENT": {
      if (!stripped.endsWith("%")) {
        const parsed = parseStrictDecimal(stripped);
        return parsed === null
          ? reject("bad-percent", raw)
          : ok(toCanonicalString(parsed));
      }
      const parsed = parseStrictDecimal(stripped.slice(0, -1).trim());
      return parsed === null
        ? reject("bad-percent", raw)
        : ok(toCanonicalString(shiftPointLeft(parsed, 2)));
    }

    case "DATE": {
      const parsed = parseDateValue(stripped);
      return parsed ? ok(parsed.dashedValue) : reject("bad-date", raw);
    }

    case "DURATION": {
      const parsed = parseEditedDurationValue(stripped);
      return parsed
        ? ok(formatDurationPartsForDisplay(parsed))
        : reject("bad-duration", raw);
    }

    case "BOOLEAN":
      return ok(stripped);
  }
}
